import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import ForgotPassword from '../components/auth/ForgotPassword';
import { claimInvite, inviteErrorCode, useInvite } from '../hooks/useInvites';
import { pickLang } from '../utils/listingDisplay';
import { INPUT_CLASS } from '../components/admin/listing/formFields';

const MIN_PASSWORD = 6;
const BUTTON = 'w-full min-h-[44px] px-4 rounded-[10px] bg-ink text-paper text-sm font-medium disabled:opacity-40';

type Mode = 'new' | 'signIn';

/**
 * Accept an invite link (plan 10.4): a new person chooses a password; someone with an
 * account signs in. Either way the listing is added to their My Items.
 */
export default function JoinPage() {
  const { token = '' } = useParams();
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const navigate = useNavigate();
  const { user, loading: authLoading, signIn, signOut } = useAuth();
  const { invite, loading } = useInvite(token);

  const [mode, setMode] = useState<Mode>('new');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (loading || authLoading) {
    return <Shell><p className="text-sm text-ash">{t('common.loading')}</p></Shell>;
  }

  const expired = invite ? new Date(invite.expiresAt) < new Date() : false;
  if (!invite || invite.used || expired) {
    const key = !invite ? 'invalid' : invite.used ? 'used' : 'expired';
    return (
      <Shell>
        <p className="text-ink">{t(`join.${key}`)}</p>
        <Link to="/login" className="text-sm text-ink underline">{t('join.goToLogin')}</Link>
      </Shell>
    );
  }

  const listingName = pickLang(invite.listingNameEn, invite.listingNameHe, isHebrew);
  const goToItem = (listingId: string) => navigate(`/admin?item=${listingId}`, { replace: true });

  const showError = (err: unknown) => {
    const code = inviteErrorCode(err);
    if (code === 'failed-precondition') {
      setMode('signIn');
      setError(t('join.error.accountExists'));
    } else if (code === 'permission-denied') {
      setError(t('join.error.otherEmail'));
    } else if (code === 'already-exists') {
      setError(t('join.used'));
    } else if (code === 'deadline-exceeded') {
      setError(t('join.expired'));
    } else if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
      setError(t('admin.loginError'));
    } else {
      setError(t('join.error.failed'));
    }
  };

  const run = async (fn: () => Promise<void>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleNew = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) return setError(t('join.error.shortPassword', { min: MIN_PASSWORD }));
    if (password !== confirm) return setError(t('join.error.mismatch'));
    run(async () => {
      const result = await claimInvite(token, password);
      await signIn(invite.email, password);
      goToItem(result.listingId);
    });
  };

  const handleSignIn = (e: React.FormEvent) => {
    e.preventDefault();
    run(async () => {
      await signIn(invite.email, password);
      const result = await claimInvite(token);
      goToItem(result.listingId);
    });
  };

  const header = (
    <div className="space-y-2">
      <h1 className="text-xl font-semibold text-ink">{t('join.title')}</h1>
      <p className="text-sm text-ink">{t('join.intro', { name: listingName })}</p>
      <p className="text-sm text-ash">
        {t('join.email')}: <span dir="ltr">{invite.email}</span>
      </p>
    </div>
  );

  // Already signed in.
  if (user) {
    const sameEmail = user.email.trim().toLowerCase() === invite.email;
    return (
      <Shell>
        {header}
        {sameEmail ? (
          <button type="button" disabled={busy} className={BUTTON} onClick={() => run(async () => goToItem((await claimInvite(token)).listingId))}>
            {t('join.addToMyItems')}
          </button>
        ) : (
          <>
            <p className="text-sm text-ink">{t('join.signedInAsOther', { email: user.email })}</p>
            <button type="button" className={BUTTON} onClick={() => signOut()}>
              {t('admin.logout')}
            </button>
          </>
        )}
        {error && <p role="alert" className="text-sm text-danger-600">{error}</p>}
      </Shell>
    );
  }

  return (
    <Shell>
      {header}
      {mode === 'new' ? (
        <form onSubmit={handleNew} className="space-y-3">
          <p className="text-sm text-ink">{t('join.choosePassword')}</p>
          <input
            type="password"
            autoComplete="new-password"
            aria-label={t('join.password')}
            placeholder={t('join.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={INPUT_CLASS}
          />
          <input
            type="password"
            autoComplete="new-password"
            aria-label={t('join.confirmPassword')}
            placeholder={t('join.confirmPassword')}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={INPUT_CLASS}
          />
          <button type="submit" disabled={busy} className={BUTTON}>
            {busy ? t('common.loading') : t('join.create')}
          </button>
        </form>
      ) : (
        <div className="space-y-2">
          <form onSubmit={handleSignIn} className="space-y-3">
            <p className="text-sm text-ink">{t('join.signInPrompt')}</p>
            <input
              type="password"
              autoComplete="current-password"
              aria-label={t('join.password')}
              placeholder={t('join.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={INPUT_CLASS}
            />
            <button type="submit" disabled={busy || !password} className={BUTTON}>
              {busy ? t('common.loading') : t('join.signIn')}
            </button>
          </form>
          <ForgotPassword email={invite.email} />
        </div>
      )}
      {error && <p role="alert" className="text-sm text-danger-600">{error}</p>}
      <button
        type="button"
        onClick={() => {
          setMode(mode === 'new' ? 'signIn' : 'new');
          setError(null);
          setPassword('');
          setConfirm('');
        }}
        className="text-sm text-ink underline min-h-[44px]"
      >
        {mode === 'new' ? t('join.haveAccount') : t('join.newHere')}
      </button>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="max-w-md mx-auto px-4 py-10 space-y-5">{children}</div>;
}
